import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    VStack,
    Grid,
    GridItem,
    Text,
    Flex,
} from "@chakra-ui/react";
import { CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";
import { MultiSelectMenu } from "../../../../components/MultiSelectMenu/MultiSelectMenu";
import { IProject, ProjectFrom, ProjectFromOptions, ServiceType, ServiceTypeOptions } from "../../model";
import { applyMask } from "../../../../utilities/masking";

interface EditProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    project: IProject | null;
    onSuccess?: () => void;
    maskingSettings?: any;
    canViewAllContacts?: boolean;
}

interface ServiceOption {
    serviceId: number;
    serviceName: string;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({
    isOpen,
    onClose,
    project,
    onSuccess,
    maskingSettings,
    canViewAllContacts,
}) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);

    const [loading, setLoading] = useState(false);
    const [servicesList, setServicesList] = useState<ServiceOption[]>([]);

    const [selectedServices, setSelectedServices] = useState<number[]>([]);
    const [serviceType, setServiceType] = useState<number>(ServiceType.Recurring);
    const [projectFrom, setProjectFrom] = useState<number | null>(null);
    const [projectStartDate, setProjectStartDate] = useState<string>("");
    const [projectEndDate, setProjectEndDate] = useState<string>("");
    const [projectAmount, setProjectAmount] = useState<string>("");
    const [projectDuration, setProjectDuration] = useState<string>("");
    const [projectDescription, setProjectDescription] = useState<string>("");

    const loadServices = async () => {
        try {
            const res = await fetchApi("Dropdown/services", "GET", null, null, "");
            if (res && Array.isArray(res)) {
                const mapped: ServiceOption[] = res.map((s: any) => ({
                    serviceId: Number(s.serviceId ?? s.ServiceId ?? s.id),
                    serviceName: s.serviceName ?? s.ServiceName ?? s.name ?? "",
                }));
                setServicesList(mapped);
            }
        } catch (error) {
            console.error("Failed to load services:", error);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadServices();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!project || !isOpen) return;

        // Set amount
        const amount = project?.project?.projectAmount ?? project?.projectAmount;
        setProjectAmount(
            amount !== undefined && amount !== null
                ? String(amount)
                : ""
        );

        // Set duration
        const dur =
            project?.project?.projectEstimatedDuration ??
            project?.project?.projectDuration ??
            project?.projectEstimatedDuration ??
            project?.projectDuration;
        setProjectDuration(dur !== undefined && dur !== null ? String(dur) : "");

        // Set Start Date
        const sDate =
            project?.project?.projectStartDate ??
            project?.project?.projecStartDate ??
            project?.projectStartDate ??
            project?.projecStartDate;
        setProjectStartDate(sDate ? String(sDate).split("T")[0] : "");

        // Set End Date
        const eDate = project?.project?.projectEndDate ?? project?.projectEndDate;
        setProjectEndDate(eDate ? String(eDate).split("T")[0] : "");

        // Set Service Type
        const rawType = project?.project?.serviceType ?? project?.serviceType;
        if (rawType !== undefined && rawType !== null) {
            if (typeof rawType === "string") {
                const matched = ServiceTypeOptions.find(
                    (opt) => opt.label.toLowerCase() === (rawType as string).toLowerCase()
                );
                setServiceType(matched ? matched.id : ServiceType.Recurring);
            } else {
                setServiceType(Number(rawType) || ServiceType.Recurring);
            }
        } else {
            setServiceType(ServiceType.Recurring);
        }

        // Set Project From
        const rawFrom =
            project?.project?.projectFrom ??
            project?.project?.ProjectFrom ??
            project?.projectFrom ??
            project?.ProjectFrom;
        if (rawFrom !== undefined && rawFrom !== null && rawFrom !== "") {
            setProjectFrom(Number(rawFrom));
        } else {
            setProjectFrom(null);
        }

        // Set Project Description
        const rawDesc =
            project?.project?.projectDescription ??
            project?.project?.description ??
            project?.projectDescription ??
            project?.description ??
            "";
        setProjectDescription(typeof rawDesc === "string" ? rawDesc.replace(/<[^>]*>/g, "").trim() : "");
    }, [project, isOpen]);

    // Parse existing service IDs once servicesList is available
    useEffect(() => {
        if (!project || !isOpen) return;

        let initialIds: number[] = [];
        const rawIds = project?.project?.serviceInterestedInIds ?? project?.serviceInterestedInIds;
        const rawServices = project?.project?.serviceInterestedIn ?? project?.serviceInterestedIn;

        if (Array.isArray(rawIds) && rawIds.length > 0) {
            initialIds = rawIds.map(Number);
        } else if (Array.isArray(rawServices)) {
            rawServices.forEach((item: any) => {
                if (typeof item === "number") {
                    initialIds.push(item);
                } else if (typeof item === "object" && item !== null) {
                    const id = item.serviceId ?? item.id;
                    if (id !== undefined && id !== null) {
                        initialIds.push(Number(id));
                    } else if (item.name || item.serviceName) {
                        const name = (item.name || item.serviceName).trim().toLowerCase();
                        const found = servicesList.find(
                            (s) => s.serviceName.trim().toLowerCase() === name
                        );
                        if (found) initialIds.push(found.serviceId);
                    }
                } else if (typeof item === "string") {
                    const name = item.trim().toLowerCase();
                    const found = servicesList.find(
                        (s) => s.serviceName.trim().toLowerCase() === name
                    );
                    if (found) initialIds.push(found.serviceId);
                }
            });
        } else if (typeof rawServices === "string" && rawServices.trim() !== "") {
            const names = rawServices.split(",").map((s) => s.trim().toLowerCase());
            names.forEach((name) => {
                const found = servicesList.find(
                    (s) => s.serviceName.trim().toLowerCase() === name
                );
                if (found) initialIds.push(found.serviceId);
            });
        }

        if (initialIds.length > 0) {
            setSelectedServices(Array.from(new Set(initialIds)));
        } else {
            setSelectedServices([]);
        }
    }, [project, isOpen, servicesList]);

    const calculateEndDate = (startDateStr: string, durationMonths: number | string): string => {
        if (!startDateStr || !durationMonths || Number(durationMonths) <= 0) return "";
        const start = new Date(startDateStr);
        if (isNaN(start.getTime())) return "";

        const d = new Date(start);
        d.setMonth(d.getMonth() + Number(durationMonths));
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleStartDateChange = (val: string) => {
        setProjectStartDate(val);
        if (val && projectDuration && Number(projectDuration) > 0) {
            const calculated = calculateEndDate(val, projectDuration);
            if (calculated) {
                setProjectEndDate(calculated);
            }
        }
    };

    const handleDurationChange = (val: string) => {
        const trimmed = val.slice(0, 3);
        setProjectDuration(trimmed);
        if (projectStartDate && trimmed && Number(trimmed) > 0) {
            const calculated = calculateEndDate(projectStartDate, trimmed);
            if (calculated) {
                setProjectEndDate(calculated);
            }
        }
    };

    const amountLabel =
        serviceType === ServiceType.Recurring || serviceType === 1 || String(serviceType).toLowerCase() === "recurring"
            ? "Monthly Budget (₹)"
            : serviceType === ServiceType.OneTime || serviceType === 2 || String(serviceType).toLowerCase() === "onetime" || String(serviceType).toLowerCase() === "one-time"
                ? "Estimated Amount (₹)"
                : "Project Amount (₹)";

    const handleSave = async () => {
        const projectId = project?.projectId || project?.id;

        console.log(project, "project");
        if (!projectId) {
            addToast({ status: "error", message: "Project ID not found." });
            return;
        }

        if (!projectAmount || Number(projectAmount) <= 0) {
            addToast({
                status: "error",
                message: "Project Amount is required and must be greater than 0.",
            });
            return;
        }

        if (!projectDuration || Number(projectDuration) <= 0) {
            addToast({
                status: "error",
                message: "Project Duration is required and must be greater than 0.",
            });
            return;
        }

        if (!projectStartDate) {
            addToast({
                status: "error",
                message: "Project Start Date is required.",
            });
            return;
        }

        if (!projectEndDate) {
            addToast({
                status: "error",
                message: "Project End Date is required.",
            });
            return;
        }

        const start = new Date(projectStartDate);
        const end = new Date(projectEndDate);
        if (end < start) {
            addToast({
                status: "error",
                message: "Project End Date cannot be before Project Start Date.",
            });
            return;
        }

        const compId =
            project?.companyId ??
            project?.project?.companyId ??
            project?.company_Id ??
            project?.company?.id;

        const payload = {
            companyId: compId,
            CompanyId: compId,
            projectAmount: Number(projectAmount),
            projectDuration: Number(projectDuration),
            projectEstimatedDuration: Number(projectDuration),
            projectStartDate: projectStartDate,
            projecStartDate: projectStartDate,
            projectEndDate: projectEndDate,
            serviceType: Number(serviceType),
            projectFrom: projectFrom !== null && projectFrom !== undefined ? Number(projectFrom) : null,
            ProjectFrom: projectFrom !== null && projectFrom !== undefined ? Number(projectFrom) : null,
            serviceInterestedInIds: selectedServices,
            serviceInterestedIn: selectedServices,
            projectDescription: projectDescription,
            description: projectDescription,
        };

        setLoading(true);
        try {
            const res = await fetchApi(`Project/${projectId}`, "PUT", payload);
            if (res) {
                addToast({
                    status: "success",
                    message: res.message || "Project details updated successfully.",
                });
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error: any) {
            console.error("Error updating project:", error);
            addToast({
                status: "error",
                message: error?.message || "Failed to update project.",
            });
        } finally {
            setLoading(false);
        }
    };

    const resolveMaskType = (field: string) => {
        if (!maskingSettings || Object.keys(maskingSettings).length === 0) return "Hidden";
        const clean = field.toLowerCase().replace(/[^a-z0-9]/g, "");
        for (const [key, val] of Object.entries(maskingSettings)) {
            const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
            if (cleanKey === clean) return val as string;
        }
        if (["company", "companyname", "projectname", "project"].includes(clean)) {
            for (const [key, val] of Object.entries(maskingSettings)) {
                const k = key.toLowerCase().replace(/[^a-z0-9]/g, "");
                if (["company", "companyname", "projectname", "project"].includes(k)) return val as string;
            }
        }
        return (maskingSettings[field] as string) || "Hidden";
    };

    const getMaskedValue = (value: string, field: string) => {
        if (!value || value === "-") return value || "";
        if (canViewAllContacts) return value;
        const maskType = resolveMaskType(field);
        return applyMask(value, maskType);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
            <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="16px" maxW="650px">
                <ModalHeader pb={2}>
                    Edit Project - {getMaskedValue(project?.projectName || project?.companyName || "Details", "company")}
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody py={4}>
                    <VStack spacing={4} align="stretch">
                        {(project?.bdmName || project?.project?.bdmName || project?.preSalesName || project?.project?.presaleName || project?.sourceName || project?.project?.sourceName) && (
                            <Flex
                                p={3}
                                bg="blue.50"
                                borderRadius="8px"
                                justify="space-between"
                                fontSize="xs"
                                color="blue.900"
                                fontWeight="500"
                            >
                                <Text>
                                    <Text as="span" color="gray.600">Source: </Text>
                                    {project?.sourceName || project?.project?.sourceName || "-"}
                                </Text>
                                <Text>
                                    <Text as="span" color="gray.600">BDM: </Text>
                                    {project?.bdmName || project?.project?.bdmName || "-"}
                                </Text>
                                <Text>
                                    <Text as="span" color="gray.600">Pre-Sales: </Text>
                                    {project?.preSalesName || project?.project?.presaleName || project?.project?.preSalesName || "-"}
                                </Text>
                            </Flex>
                        )}

                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                            {/* Service Interested In (Multi-select) */}
                            <GridItem>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Service Interested In
                                    </FormLabel>
                                    <MultiSelectMenu
                                        options={servicesList}
                                        labelKey="serviceName"
                                        valueKey="serviceId"
                                        selectedValues={selectedServices}
                                        setSelectedValues={setSelectedServices}
                                        placeholder="Select Services"
                                    />
                                </FormControl>
                            </GridItem>

                            {/* Service Type Dropdown */}
                            <GridItem>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Service Type
                                    </FormLabel>
                                    <Select
                                        value={serviceType}
                                        onChange={(e) => setServiceType(Number(e.target.value))}
                                        borderRadius="8px"
                                        bg="white"
                                    >
                                        {ServiceTypeOptions.map((opt) => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </GridItem>

                            {/* Project From Dropdown */}
                            <GridItem>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Project From
                                    </FormLabel>
                                    <Select
                                        value={projectFrom !== null && projectFrom !== undefined ? String(projectFrom) : ""}
                                        onChange={(e) => setProjectFrom(e.target.value ? Number(e.target.value) : null)}
                                        borderRadius="8px"
                                        bg="white"
                                    >
                                        <option value="">Select Project From</option>
                                        {ProjectFromOptions.map((opt) => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </GridItem>

                            {/* Project Amount / Monthly Budget / Estimated Amount */}
                            <GridItem>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        {amountLabel}
                                    </FormLabel>
                                    <Input
                                        type="number"
                                        placeholder="Enter amount"
                                        value={projectAmount}
                                        onChange={(e) => setProjectAmount(e.target.value)}
                                        borderRadius="8px"
                                        bg="white"
                                    />
                                </FormControl>
                            </GridItem>

                            {/* Project Estimated Duration */}
                            <GridItem>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Project Estimated Duration (Months)
                                    </FormLabel>
                                    <Input
                                        max={999}
                                        min={1}
                                        type="number"
                                        placeholder="Enter duration in months"
                                        value={projectDuration}
                                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (e.target.value.length > 3) {
                                                e.target.value = e.target.value.slice(0, 3);
                                            }
                                        }}
                                        onChange={(e) => handleDurationChange(e.target.value.slice(0, 3))}
                                        borderRadius="8px"
                                        bg="white"
                                    />
                                </FormControl>
                            </GridItem>

                            {/* Project Start Date */}
                            <GridItem>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Project Start Date
                                    </FormLabel>
                                    <Input
                                        type="date"
                                        value={projectStartDate}
                                        onChange={(e) => handleStartDateChange(e.target.value)}
                                        borderRadius="8px"
                                        bg="white"
                                    />
                                </FormControl>
                            </GridItem>

                            {/* Project End Date */}
                            <GridItem>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Estimated End Date
                                    </FormLabel>
                                    <Input
                                        type="date"
                                        value={projectEndDate}
                                        onChange={(e) => setProjectEndDate(e.target.value)}
                                        borderRadius="8px"
                                        bg="white"
                                    />
                                </FormControl>
                            </GridItem>

                            {/* Project Description */}
                            <GridItem colSpan={{ base: 1, md: 2 }}>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                        Project Description
                                    </FormLabel>
                                    <Textarea
                                        placeholder="Enter project description"
                                        value={projectDescription}
                                        onChange={(e) => setProjectDescription(e.target.value)}
                                        borderRadius="8px"
                                        bg="white"
                                        rows={3}
                                    />
                                </FormControl>
                            </GridItem>
                        </Grid>
                    </VStack>
                </ModalBody>

                <ModalFooter gap={3}>
                    <Button variant="ghost" onClick={onClose} isDisabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        bg="#4048D6"
                        color="white"
                        _hover={{ bg: "#353CC4" }}
                        onClick={handleSave}
                        isLoading={loading}
                    >
                        Save Changes
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditProjectModal;
